/**
 * HmsJobAuditModal — PDF/Image ↔ structured job data audit overlay
 *
 * Opens when the AI creates or attempts to create an HMS import job from an attachment.
 * Left panel:  Renders the source PDF (iframe) or image (<img>).
 * Right panel: Editable form pre-populated with AI-extracted job fields.
 * Actions:
 *   status === 'needs_confirmation' → "Confirm & Create Job" (sends corrected data to chat)
 *   status === 'created'            → "Go to Job" (navigates to the created job)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileText, Image, CheckCircle, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ChatActionResult } from '@/hooks/useChatStream';

// ─── Editable field definition ──────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  labelZh: string;
  required?: boolean;
  multiline?: boolean;
}

const JOB_FIELDS: FieldDef[] = [
  { key: 'customerName',     label: 'Customer',          labelZh: '客户',        required: true },
  { key: 'containerNo',      label: 'Container No',      labelZh: '柜号',        required: true },
  { key: 'containerSize',    label: 'Container Size',    labelZh: '柜型' },
  { key: 'blNo',             label: 'BL No',             labelZh: '提单号' },
  { key: 'doNo',             label: 'DO No',             labelZh: 'DO 号' },
  { key: 'vessel',           label: 'Vessel',            labelZh: '船名' },
  { key: 'voyage',           label: 'Voyage',            labelZh: '航次' },
  { key: 'eta',              label: 'ETA',               labelZh: '预计到港' },
  { key: 'portOfDischarge',  label: 'Port of Discharge', labelZh: '卸货港' },
  { key: 'terminal',         label: 'Terminal',          labelZh: '码头' },
  { key: 'deliveryAddress',  label: 'Delivery Address',  labelZh: '提货地址',    required: true, multiline: true },
  { key: 'pickupDepot',      label: 'Pickup Depot',      labelZh: '提箱仓' },
  { key: 'returnDepot',      label: 'Return Depot',      labelZh: '还箱仓' },
  { key: 'cargoDescription', label: 'Cargo Description', labelZh: '货物描述',    multiline: true },
  { key: 'grossWeight',      label: 'Gross Weight',      labelZh: '毛重' },
];

// ─── Left panel: PDF / Image viewer ─────────────────────────────────────────

function AttachmentViewer({
  storageKey,
  mimeType,
}: {
  storageKey: string;
  mimeType: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get<{ url?: string }>(`/storage/url?key=${encodeURIComponent(storageKey)}`)
      .then(res => {
        if (res.url) setUrl(res.url);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [storageKey]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error || !url) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
        <FileText className="h-8 w-8 opacity-40" />
        <span>无法加载文件预览</span>
      </div>
    );
  }

  const isPdf = mimeType === 'application/pdf';

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b px-3 py-1.5 bg-muted/30 shrink-0">
        {isPdf
          ? <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          : <Image className="h-3.5 w-3.5 text-muted-foreground" />
        }
        <span className="text-xs text-muted-foreground">{isPdf ? 'PDF 文件' : '图片文件'}</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          新窗口打开
        </a>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isPdf ? (
          <iframe
            src={`${url}#toolbar=0`}
            className="w-full h-full border-0"
            title="PDF 预览"
          />
        ) : (
          <div className="h-full overflow-auto flex items-start justify-center p-4 bg-muted/20">
            <img
              src={url}
              alt="附件图片"
              className="max-w-full object-contain rounded shadow"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Right panel: editable form ──────────────────────────────────────────────

function JobFieldsForm({
  fields,
  missingFields,
  onChange,
}: {
  fields: Record<string, string>;
  missingFields: string[];
  onChange: (key: string, value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 overflow-y-auto pr-1">
      {JOB_FIELDS.map(def => {
        const isMissing = missingFields.includes(def.key);
        const value = fields[def.key] ?? '';

        return (
          <div key={def.key} className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {def.label}
                <span className="ml-1 text-muted-foreground/60 text-[10px]">{def.labelZh}</span>
              </label>
              {def.required && (
                <span className="text-[10px] text-destructive font-medium">*</span>
              )}
              {isMissing && !value && (
                <Badge variant="destructive" className="text-[9px] h-3.5 px-1 py-0">缺失</Badge>
              )}
            </div>
            {def.multiline ? (
              <textarea
                rows={2}
                value={value}
                onChange={e => onChange(def.key, e.target.value)}
                className={[
                  'resize-none rounded border px-2 py-1 text-xs bg-background',
                  'focus:outline-none focus:ring-1 focus:ring-primary',
                  isMissing && !value ? 'border-destructive/60 bg-destructive/5' : 'border-input',
                ].join(' ')}
                placeholder={isMissing ? `请填写 ${def.labelZh}` : `${def.label}…`}
              />
            ) : (
              <input
                type="text"
                value={value}
                onChange={e => onChange(def.key, e.target.value)}
                className={[
                  'rounded border px-2 py-1 text-xs bg-background h-7',
                  'focus:outline-none focus:ring-1 focus:ring-primary',
                  isMissing && !value ? 'border-destructive/60 bg-destructive/5' : 'border-input',
                ].join(' ')}
                placeholder={isMissing ? `请填写 ${def.labelZh}` : `${def.label}…`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main modal ──────────────────────────────────────────────────────────────

interface Props {
  action: ChatActionResult;
  onClose: () => void;
  /** Called when user confirms (needs_confirmation path) — sends message to chat */
  onConfirm: (correctedData: Record<string, string>) => void;
}

export function HmsJobAuditModal({ action, onClose, onConfirm }: Props) {
  const navigate = useNavigate();
  const [fields, setFields] = useState<Record<string, string>>(() => ({
    ...(action.extracted ?? {}),
  }));

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFields(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleConfirm = () => {
    onConfirm(fields);
  };

  const handleGoToJob = () => {
    if (action.navigationHint?.path) {
      navigate(action.navigationHint.path);
    }
    onClose();
  };

  const isNeedsConfirmation = action.status === 'needs_confirmation';
  const isCreated = action.status === 'created';
  const hasAttachment = Boolean(action.attachmentKey);
  const mimeType = action.attachmentMimeType ?? 'application/pdf';

  // Remaining missing fields (user hasn't filled yet)
  const remainingMissing = (action.missingFields ?? []).filter(k => !fields[k]?.trim());

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 z-50 flex flex-col rounded-xl border bg-background shadow-2xl overflow-hidden md:inset-8 lg:inset-12">
        {/* Header */}
        <div className="flex items-center gap-3 border-b px-5 py-3 shrink-0">
          <div className="flex flex-col flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold truncate">
                {isCreated
                  ? `HMS 进口 Job 已创建${action.jobNumber ? ` — ${action.jobNumber}` : ''}`
                  : 'HMS 进口 Job — 审核 & 补充信息'}
              </span>
              {isCreated && (
                <Badge className="bg-green-600/90 text-white text-[10px] h-4 px-1.5">已创建</Badge>
              )}
              {isNeedsConfirmation && (
                <Badge variant="outline" className="text-amber-600 border-amber-500/50 text-[10px] h-4 px-1.5">
                  待确认
                </Badge>
              )}
            </div>
            {isNeedsConfirmation && action.missingFields && action.missingFields.length > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                以下字段缺失或信心度低：
                <span className="text-destructive font-medium ml-1">
                  {action.missingFields.join('、')}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — split panel */}
        <div className="flex flex-1 min-h-0">
          {/* Left: attachment viewer */}
          <div className={[
            'border-r flex flex-col',
            hasAttachment ? 'w-1/2 shrink-0' : 'hidden',
          ].join(' ')}>
            {hasAttachment && (
              <AttachmentViewer
                storageKey={action.attachmentKey!}
                mimeType={mimeType}
              />
            )}
          </div>

          {/* Right: editable fields */}
          <div className={[
            'flex flex-col min-h-0',
            hasAttachment ? 'flex-1' : 'w-full',
          ].join(' ')}>
            {/* Panel header */}
            <div className="flex items-center gap-2 border-b px-4 py-2 shrink-0 bg-muted/20">
              <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium">
                {isNeedsConfirmation ? '请核对并补充以下字段' : 'AI 提取结果'}
              </span>
              {isNeedsConfirmation && remainingMissing.length > 0 && (
                <div className="ml-auto flex items-center gap-1 text-xs text-amber-600">
                  <AlertTriangle className="h-3 w-3" />
                  还缺 {remainingMissing.length} 个字段
                </div>
              )}
            </div>

            {/* Field form */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              <JobFieldsForm
                fields={fields}
                missingFields={action.missingFields ?? []}
                onChange={handleFieldChange}
              />
            </div>

            {/* Footer actions */}
            <div className="border-t px-4 py-3 flex items-center justify-end gap-2 shrink-0 bg-muted/10">
              <Button variant="outline" size="sm" onClick={onClose}>
                关闭
              </Button>
              {isNeedsConfirmation && (
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={remainingMissing.length > 0}
                  title={remainingMissing.length > 0 ? `还缺：${remainingMissing.join('、')}` : undefined}
                >
                  <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                  确认并创建 Job
                </Button>
              )}
              {isCreated && (
                <Button size="sm" onClick={handleGoToJob}>
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  打开 Job 详情
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
